import { fetchBuildLogs } from '@/api/builds';
import { fetchAdminBuildLogs } from '@/api/admin';
import BuildLogViewer from '@/components/build/build-log-viewer';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { IBuild } from '@/entities/build.entity';
import type { IBuildLogLine } from '@/entities/build-live.entity';
import { cn } from '@/lib/utils';
import { ScrollTextIcon } from 'lucide-react';
import { useState } from 'react';

interface BuildLogsSectionProps {
	build: Pick<IBuild, 'id' | 'hasBuildLog' | 'failureMessage'>;
	admin?: boolean;
	className?: string;
}

export default function BuildLogsSection({ build, admin = false, className }: BuildLogsSectionProps) {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [lines, setLines] = useState<IBuildLogLine[]>([]);
	const [failureMessage, setFailureMessage] = useState<string | undefined>(build.failureMessage ?? undefined);
	const [loaded, setLoaded] = useState(false);

	if (!build.hasBuildLog) {
		return null;
	}

	async function handleToggle() {
		if (open) {
			setOpen(false);
			return;
		}

		if (!loaded) {
			setLoading(true);
			try {
				const data = admin ? await fetchAdminBuildLogs(build.id) : await fetchBuildLogs(build.id);
				setLines(data.lines);
				setFailureMessage(data.failureMessage ?? build.failureMessage ?? undefined);
				setLoaded(true);
			} finally {
				setLoading(false);
			}
		}

		setOpen(true);
	}

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			<Button type='button' variant='outline' size='sm' className='h-8 w-fit text-xs' onClick={() => void handleToggle()}>
				{loading ? <Spinner className='size-3.5' /> : <ScrollTextIcon className='size-3.5' />}
				{open ? 'Скрыть логи ошибки' : 'Логи ошибки'}
			</Button>

			{open ? (
				<div className='flex flex-col gap-2'>
					{failureMessage ? (
						<p className='rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive'>
							{failureMessage}
						</p>
					) : null}
					<BuildLogViewer lines={lines} />
				</div>
			) : null}
		</div>
	);
}
